import { Box, FormControl, FormHelperText, Grid, MenuItem, Select, Typography } from '@mui/material';
import { InputField } from '../../../components/InputController';
import { SubjectInputField } from './SubjectInputField';
import { announcementDetailStyles } from './CustomStyles';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { selectAnnouncementsState } from '../sagas/announcementSlice';
import { AnnouncementPersonalizationDto } from '@inosio/payments-sdk';
import { filterDistinctPersonalizations } from './utils/utils';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { AnnouncementSectionProps } from '../validators/AnnouncementValidators';

const validationSchema = Yup.object({
  Description: Yup.string().required('Description is required'),
  Subject: Yup.string().required('Subject is required'),
  SenderIdentityId: Yup.string().required('From is required'),
});

type schemaValidationType = Yup.InferType<typeof validationSchema>;

interface IProps extends AnnouncementSectionProps {}

export const AnnouncementDetail = (props: IProps) => {
  const { announcementDetail } = useSelector(selectAnnouncementsState);
  const { SenderIdentities } = useSelector(selectPropertyGroupDetailsState);
  const formik = useFormik<schemaValidationType>({
    initialValues: {
      Description: announcementDetail.Description || '',
      Subject: announcementDetail.Subject || '',
      SenderIdentityId: announcementDetail.SenderIdentity?.Id || '',
    },
    validationSchema: validationSchema,

    onSubmit: async (values) => {},
    enableReinitialize: true,
    validateOnMount: true,
  });

  const classes = announcementDetailStyles();

  const onHandlePersonalizeFieldsChange = (subject: string, personalizations: AnnouncementPersonalizationDto[]) => {
    props.setPendingAnnouncement({
      Subject: subject,
      Personalizations: filterDistinctPersonalizations(subject, announcementDetail.Body, personalizations),
    });
  };

  const onHandleChange = (key: keyof schemaValidationType, value: string) => {
    formik.setFieldValue(key, value);
    props.setPendingAnnouncement({
      [key]: value,
    });
  };

  return (
    <Box sx={{ padding: '20px' }}>
      <Grid container gap={5}>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
          container
        >
          <Grid item xs={2}>
            <Typography sx={{ fontWeight: 'bold', marginBottom: '20px' }}>Description:</Typography>
          </Grid>
          <Grid item xs={9}>
            <InputField
              label={undefined}
              {...formik.getFieldProps('Description')}
              name="Description"
              errors={formik.errors.Description}
              isError={!!formik.errors?.Description && formik.touched.Description}
              touched={formik.touched?.Description}
              onChange={(e) => onHandleChange('Description', e.target.value)}
              placeholder="Enter the description of your announcement detail here ex: 'Late Payment Notice'"
            />
          </Grid>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
          container
        >
          <Grid item xs={2}>
            <Typography sx={{ fontWeight: 'bold', marginBottom: '20px' }}>Subject:</Typography>
          </Grid>
          <Grid item xs={9}>
            <SubjectInputField
              value={formik.values.Subject}
              onChange={(newSubject) => onHandleChange('Subject', newSubject)}
              setFieldValue={formik.setFieldValue}
              personalizations={announcementDetail.Personalizations || []}
              onPersonalizedFieldsChange={onHandlePersonalizeFieldsChange}
              isError={formik.touched.Subject && !!formik.errors.Subject}
              error={formik.errors.Subject}
            />
          </Grid>
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
          container
        >
          <Grid item xs={2}>
            <Typography sx={{ fontWeight: 'bold', marginBottom: '20px' }}>From:</Typography>
          </Grid>
          <Grid item xs={9}>
            <FormControl sx={{ width: '100%' }} size="small">
              <Select
                displayEmpty
                className={classes.FromSelect}
                error={!!formik.errors.SenderIdentityId && formik.touched.SenderIdentityId}
                fullWidth
                {...formik.getFieldProps('SenderIdentityId')}
                onChange={(e) => onHandleChange('SenderIdentityId', e.target.value)}
              >
                <MenuItem disabled value="">
                  Select Sender
                </MenuItem>
                {SenderIdentities.map((identity) => (
                  <MenuItem key={identity.Id} value={identity.Id}>
                    {identity.Email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formik.touched.SenderIdentityId && formik.errors.SenderIdentityId ? <FormHelperText error>{formik.errors.SenderIdentityId}</FormHelperText> : null}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
